import { useCallback, useState } from 'react';
import { Accept, useDropzone } from 'react-dropzone';
import clsx from 'clsx';
import * as R from 'ramda';

import { Icon, IconButton } from '@atom/mui';
import colors from '@atom/styles/colors';

import classes from './dropzone.module.css';

const styles = {
  selectText: {
    color: colors.brand.blue,
    cursor: 'pointer',
  },
};

interface Props {
  files: File[];
  /**
   * Callback called when files are drag and dropped or selected from device.
   */
  onFilesChange: (file: File[]) => void;
  /**
   * Callback called when files are to be removed. Currently, all files will
   * be removed on click.
   */
  onFilesRemove: () => void;
  /**
   * Multiple is an optional boolean prop that allows the drag and drop or
   * selection of multiple files. Default has been set to false, which only
   * allows the ability to save 1 file at a time.
   */
  multiple?: boolean;
  /**
   * The accept attribute defines which file types can be uploaded. Keys are
   * MIME types and values are arrays of file extensions.
   * (e.g. { 'image/*': ['.jpg', '.png'], 'text/csv': ['.csv'] })
   */
  accept?: Accept;
  /**
   * Indicates some operation such as file validation is loading
   * and spinner should be shown in place of file icon
   */
  loading?: boolean;
  /**
   * Optional text to show when loading prop is true.
   * Default to "Loading..."
   */
  loadingText?: string;
}

function Dropzone(props: Props) {
  const {
    files,
    onFilesChange,
    onFilesRemove,
    accept,
    loading,
    loadingText = 'Loading...',
    multiple = false,
  } = props;

  const [isDraggedOver, setIsDraggedOver] = useState<boolean>(false);

  const onDrop = useCallback(acceptedFiles => {
    onFilesChange(acceptedFiles);
    setIsDraggedOver(false);
  }, []);

  const onDragEnter = () => {
    setIsDraggedOver(true);
  };

  const onDragLeave = () => {
    setIsDraggedOver(false);
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    onDragEnter,
    onDragLeave,
    multiple,
    accept,
  });

  const borderStyle = isDraggedOver
    ? classes.drag
    : R.isEmpty(files)
      ? classes.dashed
      : classes.solid;

  return (
    <section>
      <div
        {...getRootProps({
          className: clsx(classes.dropzone, borderStyle),
        })}
      >
        {R.isEmpty(files) ? (
          <>
            <input {...getInputProps()} />
            <div>
              <span>Drag CSV file here, or </span>
              <span style={styles.selectText}>Select from your device</span>
            </div>
          </>
        ) : (
          <div className={classes.tileContainer}>
            <div className={classes.uploadNameContainer}>
              <div className={classes.icon}>
                {loading ? (
                  <div className={classes.loading} />
                ) : (
                  <Icon color={colors.brand.blue}>insert_drive_file</Icon>
                )}
              </div>
              {loading
                ? loadingText
                : files.map((file, index) => (
                    <div className={classes.fileName} key={index}>
                      {/* @ts-ignore */}
                      {file.path}
                    </div>
                  ))}
              <IconButton size="small" onClick={onFilesRemove}>
                <Icon>close</Icon>
              </IconButton>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

export default Dropzone;
